import { type LucideIcon, ShieldCheck, User } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-13 — Shared tenant-tier primitive.
 *
 * Visual chip for the BE6 tier partition (`User.tenant_role`) on Team
 * tables and brand/agency Show team rows. Defined here in OKS-13,
 * reused verbatim by OKS-14 — single source of truth for icon + variant
 * + i18n key per tier.
 *
 * Mapping (locked — must match the OKS-12 `userTierLabel` helper at
 * `lib/user-tier-label.ts`):
 *
 *   admin  → ShieldCheck, outline-accent ("Admin")
 *   member → User,        outline        ("Member")
 *
 * The component renders ONLY for `tenant_role IN ('admin', 'member')` —
 * brand/agency users. OKS Admin (`role: admin, tenant_role: null`) and
 * Creator (`role: creator, tenant_role: null`) callers should NOT mount
 * this badge; the discriminated `null` is rejected at the type level.
 *
 * Colour is decorative — every tier combines a distinct icon + visible
 * text label so SR + colour-blind users get full information.
 *
 * The `data-state` attribute exposes the prop value verbatim — same
 * server-authoritative contract as `StatusBadge` (Vitest asserts the
 * rendered chip is keyed off `props.tier`, decoupled from any client-
 * side derivation of tier).
 */
export type TenantTier = "admin" | "member"

interface TierVisual {
  variant: "default" | "secondary" | "destructive" | "outline"
  icon: LucideIcon
  /** Class overrides per UX Spec (admin = accent outline, member = neutral outline). */
  className: string
}

const TIER_VISUAL: Record<TenantTier, TierVisual> = {
  // Admin — outline-accent. ShieldCheck signals "elevated permission".
  admin: {
    variant: "outline",
    icon: ShieldCheck,
    className:
      "border-amber-300/60 text-amber-900 dark:border-amber-700/60 dark:text-amber-200",
  },
  // Member — neutral outline. User icon signals "standard team member".
  member: {
    variant: "outline",
    icon: User,
    className: "text-muted-foreground border-muted-foreground/30",
  },
}

/**
 * Resolve the `tier.*` i18n label via literal `t()` arguments so the
 * inertia_i18n health scanner can statically prove every key is
 * reachable. Same pattern as `statusLabel` in `status-badge.tsx`.
 *
 * Note: this resolves the **shared** `tier.{admin,member}` namespace
 * (introduced here in OKS-13). The OKS-12 `userTierLabel()` helper at
 * `lib/user-tier-label.ts` resolves a richer 6-cell `(role, tenant_role)`
 * mapping under a different namespace — they coexist without collision.
 */
function tierLabel(
  t: ReturnType<typeof useTranslation>["t"],
  tier: TenantTier,
): string {
  switch (tier) {
    case "admin":
      return t("tier.admin")
    case "member":
      return t("tier.member")
  }
}

interface TierBadgeProps {
  tier: TenantTier
  /** Hide the visible label and keep only the icon. The `aria-label` still announces the tier. */
  iconOnly?: boolean
  className?: string
  /** Override the locked `data-testid`. Defaults to `agency-tier-badge` per QA contract. */
  testId?: string
}

/**
 * Renders a tenant-tier chip (icon + label + colour).
 *
 * Default `data-testid="agency-tier-badge"` matches the OKS-13 QA-locked
 * contract; OKS-14 callers override via `testId` (e.g. `brand-tier-badge`).
 * Either way the `data-state` attribute is set to the raw `tier` prop —
 * that's the contract assertion: the rendered chip is keyed off
 * `props.tier`, not derived from any client-side state.
 */
export function TierBadge({
  tier,
  iconOnly = false,
  className,
  testId = "agency-tier-badge",
}: TierBadgeProps) {
  const { t } = useTranslation()
  const visual = TIER_VISUAL[tier]
  const Icon = visual.icon
  const label = tierLabel(t, tier)

  return (
    <Badge
      variant={visual.variant}
      role="status"
      aria-label={label}
      data-testid={testId}
      data-state={tier}
      className={cn("gap-1.5", visual.className, className)}
    >
      <Icon aria-hidden="true" />
      {!iconOnly && <span>{label}</span>}
    </Badge>
  )
}
